import { type MouseEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  NARROW_FLEX_WRAP,
  NARROW_WRAP,
  StateText,
  TABLE_ICON_CLASS,
  TableCell,
  TableItem,
} from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { ToolIcon } from "@/components/tool-icon";
import { Switch } from "@/components/ui/switch";
import { type ConnectorTool, useSetPersonalToolEnabled } from "@/lib/api";
import { onControl } from "@/lib/row-click";
import { isServed } from "@/lib/tool-groups";
import { cn } from "@/lib/utils";

import { AccessLetter } from "./access-letter";
import { AudienceBadge } from "./audience-badge";
import {
  personalState,
  type Serving,
  switchErrorMessage,
  type UnavailableReason,
  unavailableReasonText,
} from "./utils";

/*
 * Less padding than the table's own between the tool and the two narrow
 * columns ("Voor", one badge, and "Voor mij"), so a tool's title line keeps
 * its room: in a window of 800 px it stays on one line for every admin role
 * in Dutch and English, the developers' wider badge included. Give the
 * headers the same.
 */

/** The tool's own cell: less padding on its right, beside "Voor" or "Voor mij". */
const TOOL_CELL_CLASS = "pr-2";

/** The "Voor" column's cells: as narrow as their badge, with little padding on either side. */
export const AUDIENCE_CELL_CLASS = "w-px px-1 whitespace-nowrap";

/**
 * The "Voor mij" column's cells: less padding on the left, and in the phone
 * layout on both sides, so the table fits a 390 px phone in every language.
 */
export const SWITCH_CELL_CLASS = "pl-2 @max-[640px]:px-2";

/** The id of the button that opens a tool's dialog, where the focus goes back when it closes. */
export function toolOpenerId(name: string): string {
  return `tool-${name}-open`;
}

interface ToolRowProps {
  tool: ConnectorTool;
  /** What the connector serves, for why a tool does not reach Claude. */
  serving: Serving;
  /**
   * Whether the row shows who the tool is for (the overview's `audience`), in
   * the "Voor" column the admin roles' table has: for them only.
   */
  showAudience?: boolean;
  /** False when the backend could not read the viewer's own switches: their switch is then disabled. */
  personalSwitchesKnown: boolean;
  /** The search as typed: its words are marked in the title, the summary and the technical name. */
  search: string;
  /** Opens the tool's dialog. */
  onOpen: (name: string) => void;
}

/**
 * One tool, for everyone: on one line its icon, its title (a button that
 * opens the tool's dialog), its technical name in muted mono and a circled
 * letter for reading or writing (`AccessLetter`); its summary under it,
 * opened by a muted "Tijdelijk uit" when a developer paused it for everyone.
 * For the admin roles who it is for ("Voor", `AudienceBadge`; on a phone
 * under the summary); and the viewer's own switch ("Voor mij",
 * `personalState`), with why Claude does not get the tool anyway when the
 * connector does not serve it. A click anywhere on the row but on its switch
 * opens the dialog, a click on the letter or the badge too, since their
 * tooltips never open on touch. A tool Claude does not give the viewer
 * (paused, switched off for themselves, or not available) is greyed out: its
 * text in the muted colour at full strength, so it stays readable, and its
 * icon and letter faded; a coming-soon tool is faded further. The switch and
 * the badge are never greyed out. No optimistic update: while a request runs
 * the switch is disabled at the server's value, and a failure is said under
 * the tool's title until that switch changes.
 */
export function ToolRow({
  tool,
  serving,
  showAudience = false,
  personalSwitchesKnown,
  search,
  onOpen,
}: ToolRowProps) {
  const { t } = useTranslation();
  const setPersonal = useSetPersonalToolEnabled();
  const { reset } = setPersonal;
  // A failure belongs to the switch as it was: once the tool is switched another way, drop it.
  useEffect(() => {
    reset();
  }, [tool.personal_enabled, reset]);
  const comingSoon = !isServed(tool);
  const reachesViewer = tool.on && tool.personal_enabled;
  const fade = comingSoon ? "opacity-50" : reachesViewer ? undefined : "opacity-60";
  const personal = personalState(tool, serving);
  // Never faded with the row: who a tool is for does not depend on whether the viewer gets it.
  const audience =
    showAudience && tool.audience !== null ? <AudienceBadge audience={tool.audience} /> : null;
  const reasonId = `tool-${tool.name}-unavailable`;
  const unavailable = personal.kind === "comingSoon" ? null : personal.unavailable;
  const error = setPersonal.isError
    ? switchErrorMessage(
        setPersonal.error,
        setPersonal.variables?.enabled ?? !tool.personal_enabled,
      )
    : null;
  // A click on the row opens the tool's dialog, also one on its letter or its badge, but not one on its switch.
  const onRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!onControl(event)) {
      onOpen(tool.name);
    }
  };

  return (
    <tr
      className="hover:bg-accent-light/40 cursor-pointer border-t"
      data-tool={tool.name}
      onClick={onRowClick}
    >
      <TableCell className={TOOL_CELL_CLASS}>
        <TableItem
          icon={<ToolIcon name={tool.name} className={cn(TABLE_ICON_CLASS, fade)} />}
          // A small gap between the title, the name and the letter, so they share a line.
          title={
            <span className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
              <button
                type="button"
                id={toolOpenerId(tool.name)}
                aria-haspopup="dialog"
                onClick={() => onOpen(tool.name)}
                className="focus-visible:ring-ring rounded-sm text-left font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
              >
                <Highlight text={tool.title} query={search} />
              </button>
              {/* Breaks anywhere, never hyphenated: a hyphen would read as part of the name. */}
              <code className="text-muted font-mono text-xs font-normal wrap-anywhere hyphens-none">
                <Highlight text={tool.name} query={search} />
              </code>
              <AccessLetter access={tool.access} className={fade} />
            </span>
          }
          // "Tijdelijk uit" opens the summary rather than ending the title line, which it would
          // make wrap; as high as the summary's line, so the line keeps its height.
          summary={
            <>
              {tool.paused && (
                <Chip className="mr-1.5 align-baseline leading-none font-normal">
                  {t("tools.paused")}
                </Chip>
              )}
              <Highlight text={tool.summary} query={search} />
            </>
          }
          // Who it is for, on a phone, where the "Voor" column leaves.
          facts={audience ?? undefined}
          muted={!reachesViewer}
        >
          {error && (
            <p role="alert" className="text-destructive mt-1 text-xs">
              {error}
            </p>
          )}
        </TableItem>
      </TableCell>
      {showAudience && (
        <TableCell narrow="hide" className={AUDIENCE_CELL_CLASS}>
          {audience}
        </TableCell>
      )}
      <TableCell align="right" className={SWITCH_CELL_CLASS}>
        {personal.kind === "switch" ? (
          // In the phone layout "Niet beschikbaar" may go above the switch, so the column stays narrow.
          <div className={cn("flex items-center justify-end gap-2", NARROW_FLEX_WRAP)}>
            {unavailable && <Unavailable id={reasonId} reason={unavailable} />}
            <Switch
              checked={tool.personal_enabled}
              onCheckedChange={(enabled) => setPersonal.mutate({ name: tool.name, enabled })}
              disabled={!personalSwitchesKnown || setPersonal.isPending}
              aria-label={t("tools.personalSwitchLabel", { title: tool.title })}
              aria-describedby={unavailable ? reasonId : undefined}
            />
          </div>
        ) : personal.kind === "alwaysOn" ? (
          unavailable ? (
            <Unavailable id={reasonId} reason={unavailable} />
          ) : (
            <StateText className={NARROW_WRAP}>{t("tools.alwaysOn")}</StateText>
          )
        ) : (
          <Chip tone="soon" className={cn(fade, NARROW_WRAP)}>
            {t("tools.comingSoon")}
          </Chip>
        )}
      </TableCell>
    </tr>
  );
}

interface UnavailableProps {
  /** The reason's id, which describes the tool's switch. */
  id: string;
  reason: UnavailableReason;
}

/**
 * "Niet beschikbaar" with its reason under it, both muted: switched on, yet
 * Claude does not get the tool. The reason stays visible, also on a phone,
 * where it may wrap, so the column stays narrow.
 */
export function Unavailable({ id, reason }: UnavailableProps) {
  const { t } = useTranslation();
  return (
    <span className="text-muted inline-flex flex-col items-end text-right text-xs leading-tight">
      <span className={cn("font-medium whitespace-nowrap", NARROW_WRAP)}>
        {t("tools.unavailable")}
      </span>
      <span id={id} className={cn("whitespace-nowrap", NARROW_WRAP)}>
        {unavailableReasonText(reason)}
      </span>
    </span>
  );
}
